import {
  ServiceHistoryStatus,
  VehicleAccidentState,
  VehicleColor,
  VehicleDrivetrain,
  VehicleKind,
  VehicleUsage,
} from "@/api-schema-enums";
import { getDateInputValidationMessage, validateDateInputString } from "@/common/helpers/date-input";
import { VIN_REGEX } from "@/common/helpers/vehicle-vin";
import { VEHICLE_VALUATION_RIM_TYPE_VALUES } from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-metadata";
import { z } from "zod";

const NON_NEGATIVE_INTEGER_PATTERN = /^\d+$/;
export const POSTGRES_INTEGER_MAX = 2_147_483_647;
const VEHICLE_BOOLEAN_SELECT_VALUES = ["true", "false"] as const;
const VEHICLE_ACCIDENT_STATE_VALUES = [
  VehicleAccidentState.UNKNOWN,
  VehicleAccidentState.AccidentFree,
  VehicleAccidentState.ExistingDamage,
  VehicleAccidentState.RepairedDamage,
] as const;
export const VEHICLE_COLOR_VALUES = [
  VehicleColor.UNKNOWN,
  VehicleColor.Beige,
  VehicleColor.Blue,
  VehicleColor.Brown,
  VehicleColor.Yellow,
  VehicleColor.Gold,
  VehicleColor.Green,
  VehicleColor.Gray,
  VehicleColor.Orange,
  VehicleColor.Red,
  VehicleColor.Black,
  VehicleColor.Silver,
  VehicleColor.White,
  VehicleColor.Purple,
] as const;
const VEHICLE_DRIVETRAIN_VALUES = [
  VehicleDrivetrain.UNKNOWN,
  VehicleDrivetrain.FrontWheelDrive,
  VehicleDrivetrain.RearWheelDrive,
  VehicleDrivetrain.FourWheelDrive,
] as const;
const SERVICE_HISTORY_VALUES = [
  ServiceHistoryStatus.UNKNOWN,
  ServiceHistoryStatus.NONE,
  ServiceHistoryStatus.PARTIAL,
  ServiceHistoryStatus.FullServiceHistory,
  ServiceHistoryStatus.FullDealerServiceHistory,
] as const;
const VEHICLE_USAGE_VALUES = [
  VehicleUsage.UNKNOWN,
  VehicleUsage.RegularPersonalCommercial,
  VehicleUsage.RENTAL,
  VehicleUsage.TaxiRideHailing,
  VehicleUsage.DrivingSchool,
  VehicleUsage.DEMO,
  VehicleUsage.FleetPool,
  VehicleUsage.DeliveryCourier,
  VehicleUsage.SpecialPurpose,
  VehicleUsage.CONSTRUCTION,
  VehicleUsage.ManagementCompanyCar,
  VehicleUsage.EmployeeCompanyCar,
] as const;
export const VEHICLE_KIND_VALUES = [
  VehicleKind.UNKNOWN,
  VehicleKind.MotorVehicle,
  VehicleKind.CommercialVehicle,
  VehicleKind.Motorcycle,
  VehicleKind.Truck,
] as const;

export type VehicleFormMode = "create" | "update";

export const VehicleFormSchema = (
  t: (key: string, options?: Record<string, unknown>) => string,
  mode: VehicleFormMode,
) =>
  z
    .object({
      modelGeneration: z.string().min(1, t("masterdata:ModelIsRequired")),
      modelDescription: z.string(),
      vin: z
        .string()
        .min(1, t("masterdata:VinIsRequired"))
        .length(17, t("masterdata:VehicleVinMustBe17Characters"))
        .regex(VIN_REGEX, t("masterdata:VehicleVinInvalidFormat")),
      licensePlate: z.string(),
      manufacturerColor: z.string(),
      color: z.union([z.literal(""), z.enum(VEHICLE_COLOR_VALUES)]),
      registrationDate: z
        .string()
        .trim()
        .superRefine((value, context) => {
          const error = validateDateInputString(value, { disallowFutureDate: true });

          if (error) {
            context.addIssue({ code: "custom", message: getDateInputValidationMessage(error) });
          }
        }),
      huDate: z
        .string()
        .trim()
        .superRefine((value, context) => {
          const error = validateDateInputString(value, { disallowFutureDate: true });

          if (error) {
            context.addIssue({ code: "custom", message: getDateInputValidationMessage(error) });
          }
        }),
      serviceHistory: z.union([z.literal(""), z.enum(SERVICE_HISTORY_VALUES)]),
      vehicleUsage: z.union([z.literal(""), z.enum(VEHICLE_USAGE_VALUES)]),
      rimType: z.union([z.literal(""), z.enum(VEHICLE_VALUATION_RIM_TYPE_VALUES)]),
      exchangeEngineInstalled: z.boolean(),
      exchangeDate: z.string().trim(),
      engineMileageAtExchange: z.string().trim(),
      vehicleMileageAtExchange: z.string().trim(),
      exchangeNotes: z.string(),
      drivetrain: z.union([z.literal(""), z.enum(VEHICLE_DRIVETRAIN_VALUES)]),
      vehicleKind: z.union([z.literal(""), z.enum(VEHICLE_KIND_VALUES)]),
      accidentState: z.union([z.literal(""), z.enum(VEHICLE_ACCIDENT_STATE_VALUES)]),
      accidentDescription: z.string(),
      accidentValue: z
        .string()
        .trim()
        .refine((value) => value === "" || (Number.isFinite(Number(value)) && Number(value) >= 0), {
          message: t("masterdata:DamageAmountMustBeANumber"),
        }),
      vatDeductible: z.union([z.literal(""), z.enum(VEHICLE_BOOLEAN_SELECT_VALUES)]),
      reimport: z.union([z.literal(""), z.enum(VEHICLE_BOOLEAN_SELECT_VALUES)]),
      engineKilowatts: z
        .string()
        .trim()
        .refine((value) => value === "" || NON_NEGATIVE_INTEGER_PATTERN.test(value), {
          message: t("masterdata:EngineKilowattsMustBeANumber"),
        })
        .refine((value) => value === "" || Number(value) <= POSTGRES_INTEGER_MAX, {
          message: t("masterdata:IntegerValueTooLarge"),
        }),
      engineDisplacementCcm: z
        .string()
        .trim()
        .refine((value) => value === "" || NON_NEGATIVE_INTEGER_PATTERN.test(value), {
          message: t("masterdata:EngineDisplacementCcmMustBeANumber"),
        })
        .refine((value) => value === "" || Number(value) <= POSTGRES_INTEGER_MAX, {
          message: t("masterdata:IntegerValueTooLarge"),
        }),
      numberDoors: z
        .string()
        .trim()
        .refine((value) => value === "" || NON_NEGATIVE_INTEGER_PATTERN.test(value), {
          message: t("masterdata:NumberDoorsMustBeANumber"),
        })
        .refine((value) => value === "" || Number(value) <= POSTGRES_INTEGER_MAX, {
          message: t("masterdata:IntegerValueTooLarge"),
        }),
      mileage: z
        .string()
        .trim()
        .refine((value) => value === "" || NON_NEGATIVE_INTEGER_PATTERN.test(value), {
          message: t("masterdata:MileageMustBeANumber"),
        })
        .refine((value) => value === "" || Number(value) <= POSTGRES_INTEGER_MAX, {
          message: t("masterdata:IntegerValueTooLarge"),
        }),
      owningBranch: mode === "create" ? z.string().min(1, t("masterdata:OwningBranchIsRequired")) : z.string(),
      currentBranch: z.string().optional(),
    })
    .superRefine((values, context) => {
      if (!values.exchangeEngineInstalled) {
        return;
      }

      if (!values.exchangeDate) {
        context.addIssue({ code: "custom", path: ["exchangeDate"], message: t("masterdata:ExchangeDateIsRequired") });
      } else {
        const dateError = validateDateInputString(values.exchangeDate, { disallowFutureDate: true });
        if (dateError) {
          context.addIssue({
            code: "custom",
            path: ["exchangeDate"],
            message: getDateInputValidationMessage(dateError),
          });
        }
      }

      if (!NON_NEGATIVE_INTEGER_PATTERN.test(values.vehicleMileageAtExchange)) {
        context.addIssue({
          code: "custom",
          path: ["vehicleMileageAtExchange"],
          message: t("masterdata:VehicleMileageAtExchangeIsRequired"),
        });
      } else if (Number(values.vehicleMileageAtExchange) > POSTGRES_INTEGER_MAX) {
        context.addIssue({
          code: "custom",
          path: ["vehicleMileageAtExchange"],
          message: t("masterdata:IntegerValueTooLarge"),
        });
      }

      if (values.engineMileageAtExchange && !NON_NEGATIVE_INTEGER_PATTERN.test(values.engineMileageAtExchange)) {
        context.addIssue({
          code: "custom",
          path: ["engineMileageAtExchange"],
          message: t("masterdata:EngineMileageAtExchangeMustBeANumber"),
        });
      } else if (Number(values.engineMileageAtExchange) > POSTGRES_INTEGER_MAX) {
        context.addIssue({
          code: "custom",
          path: ["engineMileageAtExchange"],
          message: t("masterdata:IntegerValueTooLarge"),
        });
      }
    });

export type VehicleFormSchemaType = z.infer<ReturnType<typeof VehicleFormSchema>>;
